/**
 * Emotion Configuration
 * 
 * SINGLE SOURCE OF TRUTH for all available emotions in the avatar system.
 * This file is the canonical definition - all other packages should import from here.
 */

/**
 * Full set of 24 emotions - used for training scenarios where nuanced
 * emotional expression is important for soft-skills development.
 */
export const AVAILABLE_EMOTIONS = [
  'neutral',
  'confident',
  'skeptical',
  'frustrated',
  'worried',
  'interested',
  'encouraging',
  'proud',
  'amused',
  'relieved',
  'enthusiastic',
  'attentive',
  'thinking',
  'curious',
  'impatient',
  'hesitant',
  'disappointed',
  'contemplative',
  'uncertain',
  'doubtful',
  'concerned',
  'pensive',
  'unconvinced',
  'resigned'
] as const;

/**
 * Reduced set of 10 emotions for the game mode.
 * Covers the full emotional spectrum while reducing image generation costs by ~60%.
 * 
 * Coverage:
 * - Positive: confident, encouraging
 * - Negative: frustrated, worried, disappointed
 * - Neutral: neutral
 * - Cognitive: thinking, interested
 * - Doubt: skeptical, uncertain
 * - Humor: amused (essential for comedy adventures)
 */
export const GAME_EMOTIONS = [
  'neutral',      // Base state (essential)
  'confident',    // Positive/assertive
  'skeptical',    // Doubt/questioning (covers doubtful, unconvinced)
  'frustrated',   // Irritation/negative (covers impatient)
  'worried',      // Concern/anxiety (covers concerned)
  'interested',   // Engaged/curious/attentive (covers curious, attentive, enthusiastic)
  'encouraging',  // Support/warmth
  'amused',       // Humor (essential for comedy adventures!)
  'thinking',     // Processing/reflection (covers contemplative, pensive)
  'disappointed', // Letdown/negative outcome
] as const;

export type EmotionType = typeof AVAILABLE_EMOTIONS[number];
export type GameEmotionType = typeof GAME_EMOTIONS[number];

export const DEFAULT_EMOTION: EmotionType = 'neutral';

export function isValidEmotion(emotion: string): emotion is EmotionType {
  return AVAILABLE_EMOTIONS.includes(emotion as EmotionType);
}

export function isValidGameEmotion(emotion: string): emotion is GameEmotionType {
  return GAME_EMOTIONS.includes(emotion as GameEmotionType);
}

/**
 * Map any emotion to the closest game emotion.
 * Used when the AI suggests an emotion not in the reduced game set.
 */
export function mapToGameEmotion(emotion: string): GameEmotionType {
  // Direct match
  if (isValidGameEmotion(emotion)) {
    return emotion;
  }
  
  // Map overlapping emotions to their game equivalents
  const emotionMap: Record<string, GameEmotionType> = {
    // Cognitive → thinking
    contemplative: 'thinking',
    pensive: 'thinking',
    // Interest → interested
    curious: 'interested',
    attentive: 'interested',
    enthusiastic: 'interested',
    // Doubt → skeptical
    doubtful: 'skeptical',
    unconvinced: 'skeptical',
    // Worry → worried
    concerned: 'worried',
    // Hesitation → thinking (closest neutral processing state)
    hesitant: 'thinking',
    uncertain: 'thinking',
    // Irritation → frustrated
    impatient: 'frustrated',
    // Positive → encouraging or confident
    proud: 'confident',
    relieved: 'encouraging',
    // Resignation → disappointed
    resigned: 'disappointed',
  };
  
  return emotionMap[emotion] || 'neutral';
}

export const EMOTION_DESCRIPTIONS: Record<EmotionType, string> = {
  neutral: 'Calm professional expression with composed demeanor',
  confident: 'Authoritative expression with steady, assured demeanor',
  skeptical: 'Professional doubt with questioning expression',
  frustrated: 'Contained irritation with tense features',
  worried: 'Mild concern with slightly furrowed brow and anxious eyes',
  interested: 'Engaged attention with attentive eyes and focused demeanor',
  encouraging: 'Supportive demeanor with warm smile and positive body language',
  proud: 'Quiet pride with satisfied smile and accomplished demeanor',
  amused: 'Subtle amusement with slight smirk and twinkling eyes',
  relieved: 'Quiet relief with relaxed features and eased tension',
  enthusiastic: 'Professional enthusiasm with bright eyes and energized demeanor',
  attentive: 'Focused attention with steady gaze and concentrated demeanor',
  thinking: 'Contemplative expression with thoughtful gaze and reflective demeanor',
  curious: 'Inquisitive expression with raised eyebrows and exploring gaze',
  impatient: 'Subtle impatience with pressed lips and restless energy',
  hesitant: 'Uncertain expression with slight pause and reserved demeanor',
  disappointed: 'Quiet disappointment with lowered eyes and subdued demeanor',
  contemplative: 'Deeply thoughtful expression with introspective gaze and absorbed demeanor',
  uncertain: 'Mildly uncertain expression with slight hesitation and questioning eyes',
  doubtful: 'Subtly doubtful expression with narrowed eyes and questioning look',
  concerned: 'Professionally concerned expression with attentive worry and serious demeanor',
  pensive: 'Reflective expression with inward focus and meditative demeanor',
  unconvinced: 'Professionally unconvinced expression with reserved doubt and evaluating eyes',
  resigned: 'Quietly resigned expression with acceptance in eyes and subdued demeanor',
};
